// ClientDetail/Components/Documents/RecordTable/RecordTable.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Classes from "./RecordTable.module.css";
import useScreenWidth from "../../../../../../../../../Hooks/useScreenWidth";
import SkeletonLoading from "../../../../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading";
import { Pagination, Stack } from "@mui/material";
import TableRow from "./TableRow";
import { AddIcon } from "../../../../../../../../../utils/Icons";
import { uploadMedia } from "../../../../../../../../../utils/uploadMedia";
import backend from "../../../../../../../../../api";

function asDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return String(d); }
}

export default function RecordTable({ loading, documents = [], userId }) {
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  const mapped = useMemo(() => {
    return (documents || []).map(d => ({
      _id: d._id,
      Name: d.title || d.kind || "Document",
      Type: d.kind || "-",
      UploadedBy: d.uploadedBy?.name || [d.uploadedBy?.firstName, d.uploadedBy?.lastName].filter(Boolean).join(" ") || d.uploadedBy?.email || "Admin",
      Uploaded: asDate(d.uploadedAt),
      url: d.url,
    }));
  }, [documents]);


  const [rows, setRows] = useState(mapped);
  useEffect(() => setRows(mapped), [mapped]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.max(1, Math.ceil((rows.length || 0) / itemsPerPage));
  const startIdx = (currentPage - 1) * itemsPerPage;
  const currentItems = rows.slice(startIdx, startIdx + itemsPerPage);
  const startItem = rows.length ? startIdx + 1 : 0;
  const endItem = Math.min(startIdx + itemsPerPage, rows.length);
  const totalItems = rows.length;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);

  function handlePageChange(_, pageNum) { setCurrentPage(pageNum); }

  async function onAddDocs(files) {
    if (!files?.length) return;
    try {
      const url = await uploadMedia(Array.from(files), setUploading, setPct); // returns first file url
      const f = files[0];
      const { data } = await backend.post(`/api/user/${userId}/documents`, {
        url,
        title: f?.name || "Document",
        kind: f?.type || "",
        size: f?.size || 0,
        // byUserId: <current admin id if you have it>
      });
      const updated = (data?.documents || []).map(d => ({
        _id: d._id,
        Name: d.title || d.kind || "Document",
        Type: d.kind || "-",
        UploadedBy: d.uploadedBy?.name || [d.uploadedBy?.firstName, d.uploadedBy?.lastName].filter(Boolean).join(" ") ||d.uploadedBy?.email ||"Admin",
        Uploaded: asDate(d.uploadedAt),
        url: d.url,
      }));
      setRows(updated);
      setCurrentPage(1);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to add document");
    } finally {
      setUploading(false);
      setPct(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const handleRemoved = (docId) => {
    setRows(prev => prev.filter(r => r._id !== docId));
  };

  return (
    <div className={Classes.hero}>
      <div className={Classes.table}>
        <div className={Classes.tableWrapper}>
          <div className={Classes.tableHeader}>
            <div className={`${Classes.headerItem} ${Classes.header1}`}><h5>Name</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header3}`}><h5>Type</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header4}`}><h5>Uploaded By</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header5}`}><h5>Uploaded</h5></div>
            <div className={`${Classes.headerItem} ${Classes.header7}`}>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {uploading && <h5>{pct}%</h5>}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={(e) => onAddDocs(e.target.files)}
                />
                <button
                  className={Classes.addBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  title={uploading ? "Uploading…" : "Add Document"}
                >
                  <AddIcon height="12px" fill="white" />
                  <h5>{uploading ? "Uploading…" : "Add Document"}</h5>
                </button>
              </div>
            </div>
          </div>

          <div className={Classes.tableBody}>
            {loading ? (
              <SkeletonLoading length={itemsPerPage} height={isMobile ? "3.5rem" : "2.8rem"} width="100%" br="6px" padding="0" />
            ) : currentItems && currentItems.length > 0 ? (
              currentItems.map((row, idx) => (
                <TableRow key={row._id} value={row} index={idx} userId={userId} onRemoved={handleRemoved} />
              ))
            ) : (
              <h5 className='placeholderText' style={{ margin: '10px 19px' }} >No Document yet...</h5>
            )}
          </div>

          {currentItems && currentItems.length > 0 &&
            <div className={Classes.pagFooter}>
              <h5 style={{ height: "1.2rem" }}>
                {loading ? (
                  <SkeletonLoading height="1.2rem" width="10rem" br="4px" padding="0" />
                ) : (
                  `Showing ${startItem} to ${endItem} of ${totalItems} results`
                )}
              </h5>
              <Stack spacing={2} className={Classes.pagination}>
                {loading ? (
                  <SkeletonLoading height="1.2rem" width="5rem" br="4px" padding="0" />
                ) : (
                  <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} shape="rounded" />
                )}
              </Stack>
            </div>
          }
        </div>
      </div>
    </div>
  );
}
